import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { authService } from '../services/auth.service';
import { AuthContextType, User, AuthTokens } from '../services/types/auth.types';
import { tokenManager } from '../utils/tokenManager';
import { UserPermissions } from '@/shared/enums/UserPermissions';
import { profileService } from '../services/profile.service';

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<UserPermissions[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Sincroniza o React state com o localStorage quando o interceptor atualiza o user
  // (ex: durante refresh de token que acontece fora do React context)
  // Usa custom event 'user-data-updated' para mesma aba + storage event para outras abas
  useEffect(() => {
    const syncUserFromStorage = () => {
      const storedUser = tokenManager.getUser();
      if (storedUser) {
        setUser(prev => {
          // Se o providerId mudou para undefined, preservar o anterior
          if (prev?.providerId && !storedUser.providerId) {
            return { ...storedUser, providerId: prev.providerId };
          }
          return storedUser;
        });
      }
    };

    // Custom event disparado pelo interceptor na mesma aba
    window.addEventListener('user-data-updated', syncUserFromStorage);
    // Storage event para mudanças de outras abas
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user_data') syncUserFromStorage();
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('user-data-updated', syncUserFromStorage);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (tokenManager.hasValidTokens()) {
          const storedTokens = tokenManager.getTokens();
          if (storedTokens) {
            setTokens(storedTokens);
            setIsAuthenticated(true);

            const currentUser = authService.getCurrentUser();
            if (currentUser) {
              try {
                const profile = await profileService.getProfile();
                const updatedUser = { ...currentUser };
                if (profile.photoUrl) {
                  updatedUser.photoUrl = profile.photoUrl;
                }
                setUser(updatedUser);
              } catch {
                setUser(currentUser);
              }

              const userPermissions = currentUser.permissions
                .map(p => p as UserPermissions)
                .filter(p => Object.values(UserPermissions).includes(p));

              setPermissions(userPermissions);
            }
          }
        }
        else if (tokenManager.hasRefreshToken()) {
          try {
            const { user: refreshedUser, tokens: refreshedTokens } = await authService.refreshToken();

            setTokens(refreshedTokens);
            setIsAuthenticated(true);

            try {
              const profile = await profileService.getProfile();
              const updatedUser = { ...refreshedUser };
              if (profile.photoUrl) {
                updatedUser.photoUrl = profile.photoUrl;
              }
              setUser(updatedUser);
            } catch {
              setUser(refreshedUser);
            }

            const userPermissions = refreshedUser.permissions
              .map(p => p as UserPermissions)
              .filter(p => Object.values(UserPermissions).includes(p));

            setPermissions(userPermissions);
          } catch {
            tokenManager.clearTokens();
          }
        }
      } catch {
        tokenManager.clearTokens();
      } finally {
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const { user, tokens } = await authService.login(email, password);

      try {
        const profile = await profileService.getProfile();
        const updatedUser = { ...user };
        if (profile.photoUrl) {
          updatedUser.photoUrl = profile.photoUrl;
        }
        setUser(updatedUser);
      } catch {
        setUser(user);
      }

      setTokens(tokens);
      setIsAuthenticated(true);
      setError(null);

      const userPermissions = user.permissions
        .map(p => p as UserPermissions)
        .filter(p => Object.values(UserPermissions).includes(p));

      setPermissions(userPermissions);
    } catch (err: any) {
      const errorMessage = err?.message || 'E-mail ou senha incorretos';

      setError(errorMessage);
      setIsAuthenticated(false);
      setUser(null);
      setTokens(null);

      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authService.logout();
    } catch {
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      setTokens(null);
      setError(null);
      setLoading(false);
    }
  }, []);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const { user, tokens } = await authService.refreshToken();

      setUser(user);
      setTokens(tokens);
      setIsAuthenticated(true);
      setError(null);

      return true;
    } catch {
      await logout();

      return false;
    }
  }, [logout]);

  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  const updateUser = useCallback((userData: Partial<User>): void => {
    setUser(prevUser => {
      if (!prevUser) return null;
      return { ...prevUser, ...userData };
    });
  }, []);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    tokens,
    loading,
    error,
    login,
    logout,
    clearError,
    refreshToken,
    permissions,
    updateUser,
  };

  if (!isInitialized) {
    return <></>;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
